import { TableView1 } from "..";
import React, { useContext, useEffect, useMemo, useState } from "react";
import { useLang, useTextDomainContext, useUser } from "@/app/providers";
import * as PropsType from "prop-types";
import {
  energyMonthlyColumns,
  energyStatementKey,
  getMonthFromString,
  getMonthsArray,
  indexedDbGetById,
  indexedDbGetOnePageData,
} from "@/utils";

type EnergyMonthlyStatementProps = {
  onChangeEnergyStatement?: (obj: Record<string, unknown>) => void;
  fileNameForExport?: string;
  meterType: string;
  year: number;
  month: string;
  excelData?: unknown[];
  infoText?: string;
  loading?: boolean;
  isClimate?: boolean;
};

export const EnergyMonthlyStatement = (props: EnergyMonthlyStatementProps) => {
  const { TextDomainContext } = useTextDomainContext();
  const { gettext } = useContext(TextDomainContext);
  const locale = useLang();
  const { user } = useUser();
  let { currentTeam: customer, currentSubsidiary: group } = user || {};

  if (!customer) {
    customer = "all";
  }

  if (!group) {
    group = "All";
  }

  const initialState = {
    pagination: {
      paginationModel: {
        page: 0,
        pageSize: 10,
      },
    },
  };

  //#region React Hooks (useState)

  const propsDefault = {
    title: gettext("One Month Energy Consumption (kWh)"),
    rowCount: -1,
  };

  const [columns, setColumns] = useState([]);
  const [rows, setRows] = useState([]);
  const [rowCount, setRowCount] = useState(propsDefault.rowCount);
  const [localLoading, setLocalLoading] = useState(false);
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 100,
  });
  const [filterModel, setFilterModel] = useState({
    items: [],
  });
  const [sortModel, setSortModel] = useState([]);

  // Use props directly or calculate with useMemo instead of state
  const fileNameForExport =
    props.fileNameForExport || "energy-monthly-statement";
  const infoText = props.infoText || "";

  // Calculate fullMonthName using useMemo
  const fullMonthName = useMemo(() => {
    const monthList = getMonthsArray(gettext);
    if (props.month === "all") {
      return gettext("Full Year");
    } else {
      return monthList.find((obj) => obj.value === props.month)?.label || "";
    }
  }, [gettext, props.month]);

  // Calculate title using useMemo
  const title = useMemo(() => {
    if (props.isClimate) {
      return fullMonthName + " " + gettext("CO₂e (Kg)");
    } else {
      return fullMonthName + " " + gettext("Energy Consumption (kWh)");
    }
  }, [fullMonthName, gettext, props.isClimate]);

  // Calculate filters using useMemo
  const filters = useMemo(
    () => ({
      meterType: props.meterType,
      customer: customer,
      group: group,
      year: props.year,
      isClimate: props.isClimate,
      month: getMonthFromString(props.month),
    }),
    [
      customer,
      group,
      props.isClimate,
      props.meterType,
      props.month,
      props.year,
    ],
  );

  //#endregion React Hooks (useState)

  //#region React Hooks (useEffect)

  useEffect(() => {
    const newFilter = Object.fromEntries(
      Object.entries(filters).filter(([key]) => !["month"].includes(key)),
    );

    const keyMonthData = energyStatementKey("monthData4Columns", newFilter);

    if (filters?.customer) {
      indexedDbGetById("cache", keyMonthData)
        .then((result) => {
          const columnsData = energyMonthlyColumns(
            result,
            gettext,
            locale,
            "month",
          );
          setColumns(columnsData);
        })
        .catch();
    }
  }, [filters, gettext, locale]);

  useEffect(() => {
    const loadData = async () => {
      setLocalLoading(true);

      if (filters?.meterType) {
        try {
          const key = energyStatementKey(props.month, filters);
          const result: any = await indexedDbGetOnePageData(
            "cache",
            key,
            paginationModel,
            sortModel,
            filterModel,
          );

          if (result?.data?.length) {
            setRowCount(result.rowCount);
            setRows(result.data);
          } else if (!props.loading) {
            if (result?.data) {
              setRowCount(result.rowCount);
              setRows(result.data);
            } else {
              setRowCount(0);
              setRows([]);
            }
          }
        } catch {
          // Handle error silently
        } finally {
          setLocalLoading(false);
        }
      } else {
        setLocalLoading(false);
      }
    };

    loadData();
  }, [
    filterModel,
    filters,
    paginationModel,
    props.loading,
    props.month,
    sortModel,
  ]);

  //#endregion React Hooks (useEffect)

  //#region Handlers

  const handleChangeEnergyStatement = (obj: Record<string, unknown>) => {
    if (typeof props.onChangeEnergyStatement === "function") {
      props.onChangeEnergyStatement(obj);
    }
  };

  //#endregion Handlers

  return (
    <>
      {/*{columns.length > 0 && (*/}
      <TableView1
        title={title}
        // rows={metersData}
        rows={rows}
        // rows={[]}
        rowCount={rowCount}
        columns={columns}
        // loading={loading || localLoading}
        loading={localLoading}
        initialState={initialState}
        showGridToolbar
        // showEnergyStatementType
        showGridToolbarEnergyStatementExport
        // showGridToolbarQuickFilter
        fileNameForExport={fileNameForExport}
        excelData={props.excelData}
        onChangeEnergyStatement={handleChangeEnergyStatement}
        onPaginationModelChange={setPaginationModel}
        onSortModelChange={setSortModel}
        onFilterModelChange={setFilterModel}
        dataMode="server"
        infoText={infoText}
        // selectedEnergyStatement="monthly-statement"
      />
      {/*)}*/}
      {/*{loading && (*/}
      {/*  TableLoadingStateEnergyStatement()*/}
      {/*  // gettext("Loading")*/}
      {/*)}*/}
      {/*{!loading && !data?.consumptionEnergyData?.consumptionData?.length && (*/}
      {/*  TableLoadingStateEnergyStatement()*/}
      {/*  // gettext("Still Loading")*/}
      {/*)}*/}

      {/*{!loading && data?.consumptionEnergyData?.consumptionData.length > 0 && !rows.length && (*/}
      {/*  TableLoadingStateEnergyStatement()*/}
      {/*)}*/}

      {/*{!loading && !(Array.isArray(data?.consumptionEnergyData?.consumptionData) && data?.consumptionEnergyData?.consumptionData.length > 0 && data?.consumptionEnergyData?.consumptionData[0]?.details?.data) && (*/}
      {/*  <>*/}
      {/*    {gettext("No data found")}<br/>*/}
      {/*    {gettext("If the data is heavy, it may still be loading.")}<br/>*/}
      {/*  </>*/}
      {/*)}*/}

      {/*{!loading && columns.length === 0 && (*/}
      {/*  <>*/}
      {/*    {gettext("No data found")}<br/>*/}
      {/*    {gettext("If the data is heavy, it may still be loading.")}<br/>*/}
      {/*  </>*/}
      {/*)}*/}
    </>
  );
};

EnergyMonthlyStatement.propTypes = {
  onChangeEnergyStatement: PropsType.func,
  fileNameForExport: PropsType.string,
  meterType: PropsType.string.isRequired,
  year: PropsType.number.isRequired,
  month: PropsType.string.isRequired,
  // columns: PropsType.array.isRequired,
  // rows: PropsType.array.isRequired,
  excelData: PropsType.array,
  // rowCount: PropsType.number,
  infoText: PropsType.string,
  loading: PropsType.bool,
  isClimate: PropsType.bool,
};
